// Re-export shared platform utilities for backward compatibility
export {
  getPlatformIcon as getClientIcon,
  getPlatformLabel as getClientLabel,
  platformLabels as clientLabels,
  type IconComponent,
} from '@/components/shared/platform';

import type {
  ClientActivity,
  CoverageStatus,
} from '@kirin/api-client/contracts/user-security-posture';
import {
  COVERAGE_STATUS_ORDER,
  COVERAGE_TOOLTIPS,
} from '@kirin/api-client/contracts/user-security-posture';
import type { LoggedInUserMatch } from '@kirin/api-client/contracts/shadow-ai';

/**
 * Tailwind chip classes for a flagged logged-in account, by match severity
 * (KIR-3657): a `personal` account (domain outside the org) is painted red, a
 * `corporate` account (approved domain, different local-part) amber — matching
 * the Shadow AI tabs' spectrum rather than collapsing both to red. Shared across
 * the posture badge, the client-details modal, and the expanded row. Callers add
 * their own shape (rounded pill / border).
 */
const LOGGED_IN_PERSONAL_CLASS =
  'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
const LOGGED_IN_CORPORATE_CLASS =
  'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';

/**
 * Chip class for a flagged logged-in account by match: `personal` → red,
 * `corporate` → amber (KIR-3657). Empty string when the account is not flagged
 * (`matched`/`unknown`/no email) so callers can concatenate unconditionally.
 */
export function loggedInMatchChipClass(
  match: LoggedInUserMatch | undefined
): string {
  if (match === 'personal') return LOGGED_IN_PERSONAL_CLASS;
  if (match === 'corporate') return LOGGED_IN_CORPORATE_CLASS;
  return '';
}

/**
 * Border class matching {@link loggedInMatchChipClass}, for the badge shape that
 * draws its own border (KIR-3657).
 */
export function loggedInMatchBorderClass(
  match: LoggedInUserMatch | undefined
): string {
  if (match === 'personal') return 'border-red-200 dark:border-red-900';
  if (match === 'corporate') return 'border-amber-200 dark:border-amber-900';
  return '';
}

/**
 * A client is a "logged-in mismatch" when an account was extracted and it is not
 * the Kirin user's own account (corporate same-domain or personal) — KIR-2916.
 * `unknown`/no-email clients are not mismatches (nothing to flag). Used for the
 * mismatched-first sort and to gate the explanatory tooltip; the *colour* of the
 * flag is chosen separately by match via {@link loggedInMatchChipClass}
 * (personal = red, corporate = amber) — KIR-3657.
 */
export function isLoggedInUserMismatch(
  loggedInUserEmail: string | null | undefined,
  match: LoggedInUserMatch | undefined
): boolean {
  return !!loggedInUserEmail && (match === 'personal' || match === 'corporate');
}

/**
 * The reason a logged-in account is flagged, by match type (KIR-3429).
 * `personal` = domain outside the org (red); `corporate` = different account on
 * an approved domain (amber). Non-mismatch matches have no reason.
 */
export function loggedInMismatchReason(
  match: LoggedInUserMatch | undefined
): string | null {
  if (match === 'personal') {
    return 'This account belongs to a domain outside the approved organization domains';
  }
  if (match === 'corporate') {
    return 'This account is different than the user’s email but belongs to an approved domain';
  }
  return null;
}

/**
 * Builds the logged-in account tooltip shared by the posture badge and the
 * expanded-row account list (KIR-2916, KIR-3429): leads with the mismatch
 * account + why it is flagged, then coverage status, then last-active time.
 * Returns undefined when there is nothing worth showing.
 */
export function buildLoggedInTooltip(
  coverageStatus: CoverageStatus | undefined,
  lastActiveAt: string | null | undefined,
  match: LoggedInUserMatch | undefined,
  loggedInUserEmail: string | null | undefined
): string | undefined {
  const parts: string[] = [];

  // The logged-in mismatch is the most important signal — lead with it.
  if (isLoggedInUserMismatch(loggedInUserEmail, match)) {
    parts.push(`Logged in as ${loggedInUserEmail}`);
    const reason = loggedInMismatchReason(match);
    if (reason) parts.push(reason);
  }

  if (coverageStatus && COVERAGE_TOOLTIPS[coverageStatus]) {
    parts.push(COVERAGE_TOOLTIPS[coverageStatus]);
  }

  if (lastActiveAt) {
    parts.push(`Last active: ${formatFullTimestamp(lastActiveAt)}`);
  }

  return parts.length > 0 ? parts.join(' · ') : undefined;
}

/**
 * Stable sort surfacing clients whose logged-in account is a mismatch
 * (personal/corporate) to the front, so red badges are never hidden behind the
 * "+N more" truncation in the posture table (KIR-2916). Otherwise order is
 * preserved (Array.prototype.sort is stable).
 */
export function sortClientsMismatchedFirst(
  clients: ClientActivity[]
): ClientActivity[] {
  return [...clients].sort((a, b) => {
    const aRank = isLoggedInUserMismatch(
      a.loggedInUserEmail,
      a.loggedInUserMatch
    )
      ? 0
      : 1;
    const bRank = isLoggedInUserMismatch(
      b.loggedInUserEmail,
      b.loggedInUserMatch
    )
      ? 0
      : 1;
    return aRank - bRank;
  });
}

export function groupClientsByCoverage(
  clients: ClientActivity[]
): Record<CoverageStatus, ClientActivity[]> {
  const groups: Record<CoverageStatus, ClientActivity[]> = {
    covered: [],
    supported: [],
    unknown: [],
  };
  const knownStatuses = new Set<string>(COVERAGE_STATUS_ORDER);
  for (const c of clients) {
    const status = knownStatuses.has(c.coverageStatus)
      ? (c.coverageStatus as CoverageStatus)
      : 'unknown';
    groups[status].push(c);
  }
  return groups;
}

/**
 * Format a full UTC timestamp for display
 * Output: "2025-12-11 10:40:22.654 UTC"
 */
export function formatFullTimestamp(isoString: string): string {
  const date = new Date(isoString);
  return date.toISOString().replace('T', ' ').replace('Z', ' UTC');
}
